import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Modal, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';

import { createPlatformAdmin, listUsers } from '../../api/resources';
import { DataPanel } from '../../components/DataPanel';
import { DetailDrawer } from '../../components/detail/DetailDrawer';
import { UserDetailPanel } from '../../components/detail/UserDetailPanel';
import { EntityRef } from '../../components/EntityRef';
import { PageHeader } from '../../components/PageHeader';
import { PaginatedTable } from '../../components/PaginatedTable';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { DEFAULT_PAGE_SIZE, useListQuery } from '../../hooks/useListQuery';
import { userLabel } from '../../lib/labels';

export function UsersPage() {
    const { t } = useTranslation();
    const [page, setPage] = useState(1);
    const [detailUserId, setDetailUserId] = useState<string | null>(null);
    const [emailFilter, setEmailFilter] = useState('');
    const debouncedEmail = useDebouncedValue(emailFilter);
    const [createOpen, setCreateOpen] = useState(false);
    const queryClient = useQueryClient();

    const { data, isLoading } = useListQuery('users', listUsers, {
        page,
        email: debouncedEmail || undefined,
    });

    const form = useForm({
        initialValues: {
            email: '',
            firstName: '',
            lastName: '',
            phone: '',
        },
    });

    const createMutation = useMutation({
        mutationFn: createPlatformAdmin,
        onSuccess: () => {
            notifications.show({ color: 'green', message: t('features.users.created') });
            setCreateOpen(false);
            form.reset();
            void queryClient.invalidateQueries({ queryKey: ['users'] });
        },
        onError: (e: Error) => notifications.show({ color: 'red', message: e.message }),
    });

    return (
        <Stack gap="lg">
            <PageHeader
                title={t('features.users.title')}
                description={t('features.users.description')}
                actions={
                    <Button onClick={() => setCreateOpen(true)}>
                        {t('features.users.createAction')}
                    </Button>
                }
            />
            <DataPanel>
                <TextInput
                    label={t('features.users.filterEmail')}
                    placeholder={t('common.placeholders.emailFilter')}
                    value={emailFilter}
                    onChange={(e) => {
                        setEmailFilter(e.currentTarget.value);
                        setPage(1);
                    }}
                />
            </DataPanel>
            <PaginatedTable
                rowKey={(r) => r.id}
                columns={[
                    {
                        key: 'name',
                        header: t('fields.user'),
                        render: (r) => (
                            <EntityRef
                                title={userLabel(r)}
                                id={r.id}
                                secondary={r.email ?? r.phone}
                            />
                        ),
                    },
                    {
                        key: 'phone',
                        header: t('fields.phone'),
                        render: (r) => r.phone ?? t('common.empty'),
                    },
                    {
                        key: 'admin',
                        header: t('fields.platformAdmin'),
                        render: (r) =>
                            r.isPlatformAdmin ? (
                                <Badge color="violet" variant="light">
                                    {t('badges.admin')}
                                </Badge>
                            ) : (
                                <Badge variant="default">{t('badges.user')}</Badge>
                            ),
                    },
                ]}
                rows={data?.data ?? []}
                total={data?.total ?? 0}
                page={page}
                pageSize={DEFAULT_PAGE_SIZE}
                onPageChange={setPage}
                isLoading={isLoading}
                onRowClick={(r) => setDetailUserId(r.id)}
            />
            <DetailDrawer
                opened={Boolean(detailUserId)}
                onClose={() => setDetailUserId(null)}
                title={t('detail.user')}
            >
                {detailUserId ? <UserDetailPanel userId={detailUserId} /> : null}
            </DetailDrawer>
            <Modal
                opened={createOpen}
                onClose={() => setCreateOpen(false)}
                title={t('features.users.createModalTitle')}
            >
                <Stack>
                    <TextInput label={t('fields.email')} required {...form.getInputProps('email')} />
                    <TextInput
                        label={t('fields.firstName')}
                        required
                        {...form.getInputProps('firstName')}
                    />
                    <TextInput
                        label={t('fields.lastName')}
                        required
                        {...form.getInputProps('lastName')}
                    />
                    <TextInput label={t('fields.phone')} required {...form.getInputProps('phone')} />
                    <Button
                        loading={createMutation.isPending}
                        onClick={() => createMutation.mutate(form.values)}
                    >
                        {t('common.actions.create')}
                    </Button>
                </Stack>
            </Modal>
        </Stack>
    );
}
